import React, { useState } from 'react';
import './Accordion.scss';

export interface AccordionItem {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface AccordionProps {
  items: AccordionItem[];

  allowMultipleOpen?: boolean;

  className?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  items,
  allowMultipleOpen = false,
  className,
}) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string, disabled?: boolean) => {
    if (disabled) return;
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      const isOpen = newSet.has(id);
      if (allowMultipleOpen) {
        // Toggle individually
        if (isOpen) newSet.delete(id);
        else newSet.add(id);
      } else {
        // Single open at a time
        newSet.clear();
        if (!isOpen) newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div className={['accordion', className].filter(Boolean).join(' ')}>
      {items.map(({ id, title, content, disabled }) => {
        const isOpen = openItems.has(id);
        return (
          <div
            key={id}
            className={[
              'accordion__item',
              isOpen ? 'accordion__item--open' : undefined,
              disabled ? 'accordion__item--disabled' : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              className="accordion__header"
              aria-expanded={isOpen}
              aria-controls={`${id}-panel`}
              id={`${id}-header`}
              disabled={disabled}
              onClick={() => toggleItem(id, disabled)}
            >
              {title}
              <span className="accordion__icon" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <div
              id={`${id}-panel`}
              role="region"
              aria-labelledby={`${id}-header`}
              className="accordion__panel"
              style={{ display: isOpen ? 'block' : 'none' }}
            >
              {content}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
